import {
  QtmButton,
  QtmFormCaption,
  QtmFormField,
  QtmFormLabel,
  QtmIcon,
  QtmTextInput,
} from '@qtm/react';
import { useCreateUserMutation, useUpdateUserMutation } from 'api/UsersEndPoints';
import useUserResolver from 'modules/Users/createUser/useUserResolver';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from 'shared/model/user.model';

type Props = {
  model: Omit<User, 'id'>;
  existingUsers: User[];
};

const UserForm = ({ model, existingUsers }: Props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';

  const resolver = useUserResolver(existingUsers);

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Omit<User, 'id'>>({
    defaultValues: {
      ...model,
    },
    resolver,
  });

  const onSubmit = async (data: Omit<User, 'id'>) => {
    if (isNew) {
      await createUser(data).unwrap();
    } else {
      await updateUser({
        id: Number(id),
        ...data,
      }).unwrap();
    }
    navigate('/users');
  };

  const onCancel = () => {
    navigate('/users');
  };

  const isDisabled = isCreating || isUpdating;

  const title = isNew ? 'Create user' : 'Edit user';

  return (
    <div className="user-form">
      <div className="page-title">{title}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="field">
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <QtmFormField>
                <QtmFormLabel>First Name</QtmFormLabel>
                <QtmTextInput
                  {...field}
                  onValueChanged={field.onChange}
                  disabled={isDisabled}
                  autoFocus
                />
                {errors?.firstName && (
                  <QtmFormCaption state="error">{errors.firstName.message}</QtmFormCaption>
                )}
              </QtmFormField>
            )}
          />
        </div>
        <div className="field">
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => {
              return (
                <QtmFormField>
                  <QtmFormLabel>Last Name</QtmFormLabel>
                  <QtmTextInput {...field} onValueChanged={field.onChange} disabled={isDisabled} />
                  {errors?.lastName && (
                    <QtmFormCaption state="error">{errors.lastName.message}</QtmFormCaption>
                  )}
                </QtmFormField>
              );
            }}
          />
        </div>
        <div className="field">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <QtmFormField>
                <QtmFormLabel>Email</QtmFormLabel>
                <QtmTextInput {...field} onValueChanged={field.onChange} disabled={isDisabled} />
                {errors?.email && (
                  <QtmFormCaption state="error">{errors.email.message}</QtmFormCaption>
                )}
              </QtmFormField>
            )}
          />
        </div>
        <div className="field">
          <Controller
            name="company"
            control={control}
            render={({ field }) => (
              <QtmFormField>
                <QtmFormLabel>Company</QtmFormLabel>
                <QtmTextInput {...field} onValueChanged={field.onChange} disabled={isDisabled} />
                {errors?.company && (
                  <QtmFormCaption state="error">{errors.company.message}</QtmFormCaption>
                )}
              </QtmFormField>
            )}
          />
        </div>
        <div className="form-buttons">
          <QtmButton type="button" variant="outline" disabled={isDisabled} onClick={onCancel}>
            <QtmIcon icon="cancel" />
            <span>Cancel</span>
          </QtmButton>
          <QtmButton type="submit" disabled={isDisabled}>
            <QtmIcon icon="save" />
            <span>Save</span>
          </QtmButton>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
